import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';

const AuthContext = createContext(null);

const API_URL = API_BASE_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [userFeatures, setUserFeatures] = useState([]); // Features user has access to
  const [restrictedFeatures, setRestrictedFeatures] = useState([]); // Features blocked by plan
  const [planFeatures, setPlanFeatures] = useState(null); // Detailed plan feature flags
  const [permissionsLoaded, setPermissionsLoaded] = useState(false); // Track if permissions have been fetched

  // Fetch user's accessible features from backend
  const fetchUserPermissions = useCallback(async (accessToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/my-permissions`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      setUserFeatures(response.data.features || []);
      setRestrictedFeatures(response.data.restricted_features || []);
      setPlanFeatures(response.data.plan_features || null);
      setPermissionsLoaded(true);
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch user permissions:', error);
      // Default to empty - will use static permissions as fallback
      setUserFeatures([]);
      setRestrictedFeatures([]);
      setPlanFeatures(null);
      setPermissionsLoaded(true); // Mark as loaded even on error to stop showing loading state
      return { features: [] };
    }
  }, []);

  // Check token validity on mount
  useEffect(() => {
    const validateToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setPermissionsLoaded(true);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${storedToken}` }
        });
        setUser(response.data);
        setToken(storedToken);
        
        // Fetch user's accessible features
        await fetchUserPermissions(storedToken);
      } catch (error) {
        console.error('Token validation error:', error);
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setUserFeatures([]);
        setPermissionsLoaded(true);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [fetchUserPermissions]);

  // Check if user exists
  const checkUser = async (phoneNumber) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/check-user`, {
        params: { phone_number: phoneNumber }
      });
      return response.data;
    } catch (error) {
      console.error('Check user error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to check user');
    }
  };

  // Login for existing users
  const login = async (phoneNumber, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        phone_number: phoneNumber,
        password
      });
      
      const data = response.data;
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('last_phone', phoneNumber);
      setToken(data.access_token);
      setUser(data.user);
      
      // Fetch user's accessible features
      await fetchUserPermissions(data.access_token);
      
      // Sync call history in the background after login
      syncCallHistoryInBackground(data.access_token);
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };
  
  // Background sync for call history after login
  const syncCallHistoryInBackground = async (accessToken) => {
    try {
      await axios.post(`${API_URL}/api/cdr/sync`, null, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      console.log('Call history synced after login');
    } catch (error) {
      // Don't fail login if sync fails - just log it
      console.warn('Background CDR sync failed:', error);
    }
  };

  // First-time login/registration
  const firstLogin = async (phoneNumber, password, sipDomain, accountNumber, userName) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/first-login`, {
        phone_number: phoneNumber,
        password,
        sip_domain: sipDomain,
        account_number: accountNumber,
        name: userName
      });
      
      const data = response.data;
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('last_phone', phoneNumber);
      setToken(data.access_token);
      setUser(data.user);
      
      // Fetch user's accessible features
      await fetchUserPermissions(data.access_token);
      
      // Sync call history in the background after first login too
      syncCallHistoryInBackground(data.access_token);
      
      return data;
    } catch (error) {
      console.error('First login error:', error);
      throw new Error(error.response?.data?.detail || 'Registration failed');
    }
  };

  // Get available SIP domains
  const getSipDomains = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/sip-domains`);
      return response.data;
    } catch (error) {
      console.error('Get SIP domains error:', error);
      return [];
    }
  };

  // Setup SIP password for imported users
  const setupSip = async (sipPassword) => {
    try {
      await axios.post(`${API_URL}/api/auth/setup-sip`, 
        { sip_password: sipPassword },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Update local user state
      setUser(prev => ({ ...prev, sip_password: sipPassword, needs_sip_setup: false }));
      return true;
    } catch (error) {
      console.error('Setup SIP error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to setup SIP password');
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setUserFeatures([]);
    setRestrictedFeatures([]);
    setPlanFeatures(null);
    setPermissionsLoaded(false);
  };

  // Refresh user data from server
  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  };

  // Role check helpers
  const isDeveloper = () => user?.role === 'developer';
  const isCustomerAdmin = () => ['developer', 'customer_admin'].includes(user?.role);
  const isPhoneAdmin = () => ['developer', 'customer_admin', 'phone_admin'].includes(user?.role);
  const needsSipSetup = () => user?.needs_sip_setup === true;
  
  // Check if user has access to a specific feature (from backend permissions)
  const hasFeatureAccess = (featureId) => {
    // If permissions not loaded yet, be restrictive - hide features until we know
    if (!permissionsLoaded) {
      // Only show basic features that all authenticated users have
      const basicFeatures = ['dialer', 'contacts', 'my_calls', 'profile'];
      return basicFeatures.includes(featureId);
    }
    return userFeatures.includes(featureId);
  };
  
  // Check if a feature is restricted by plan (not role)
  const isFeatureRestrictedByPlan = (featureId) => {
    return restrictedFeatures.some(r => r.feature_id === featureId);
  };
  
  // Get restriction reason for a feature
  const getFeatureRestrictionReason = (featureId) => {
    const restriction = restrictedFeatures.find(r => r.feature_id === featureId);
    return restriction?.reason || null;
  };
  
  // Check if a specific plan feature is enabled
  const hasPlanFeature = (featureKey) => {
    if (!planFeatures) return true; // Assume enabled if not loaded
    return planFeatures[featureKey] === true || planFeatures[featureKey] === null; // null means unlimited
  };
  
  // Get current plan name
  const getCurrentPlan = () => user?.plan || planFeatures?.plan || 'free';

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    userFeatures,
    restrictedFeatures,
    planFeatures,
    permissionsLoaded,
    hasFeatureAccess,
    isFeatureRestrictedByPlan,
    getFeatureRestrictionReason,
    hasPlanFeature,
    getCurrentPlan,
    refreshPermissions: () => fetchUserPermissions(token),
    login,
    firstLogin,
    logout,
    checkUser,
    getSipDomains,
    setupSip,
    refreshUser,
    isDeveloper,
    isCustomerAdmin,
    isPhoneAdmin,
    needsSipSetup
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
